// Translate technical/browser error strings into user-facing copy seniors
// won't mistake for the app being broken. The browser's fetch() throws
// a literal `TypeError: Failed to fetch` on any network failure, and
// shipping that string to a 75-year-old is the round-4 #2 complaint.
//
// Use anywhere you would otherwise render `err.message`:
//
//   try { ... }
//   catch (err) { setError(humanizeError(err)); }
//
// For the visual treatment + Retry button, see <ApiError>.

const PATTERNS: Array<{ test: (s: string) => boolean; humane: string }> = [
  {
    // Chromium + Firefox: "TypeError: Failed to fetch", Safari: "Load failed",
    // both surface when the browser can't reach the API host (offline,
    // DNS fail, TLS error, CORS).
    test: (s) => /failed to fetch|load failed|network ?error|networkerror/i.test(s),
    humane:
      'We could not reach MedReview just now. Please check your internet and try again. Your data is safe.',
  },
  {
    // Our own UnauthenticatedError + 401 path.
    test: (s) => /not authenticated|unauthorized|401|session expired/i.test(s),
    humane: 'Your sign-in expired. Please sign in again to keep going.',
  },
  {
    // Generic 5xx surfaced via "HTTP 5xx" strings from useAuthedFetch.
    test: (s) => /\b5\d\d\b|server error/i.test(s),
    humane:
      'Something went wrong on our end. Try again in a moment. If it keeps happening, email support@otchealthmart.com.',
  },
  {
    // Generic 4xx — usually a validation issue. Leave the original
    // message visible because it's usually informative ("Invalid email").
    test: (s) => /\b4\d\d\b/.test(s),
    humane: '', // sentinel: fall through to original
  },
];

const FALLBACK = 'Something went wrong. Please try again in a moment.';

export function humanizeError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (!raw) return FALLBACK;
  for (const p of PATTERNS) {
    if (p.test(raw)) {
      return p.humane || raw;
    }
  }
  // Final defense: if the raw text reads like a stack-trace prefix or a
  // technical exception name, swap to fallback. Otherwise surface as-is
  // (validation strings like "Please enter a valid email" should pass).
  if (/^[A-Z][a-zA-Z]+Error:|^Uncaught|^at\s/.test(raw)) return FALLBACK;
  return raw;
}
